<template>
  <div class="mobile-header">
    <div class="mobile-header--logo">
      <Button @click="toggleSidebar()" severity="sidebar">
        <IconSidebarCollapse v-if="sidebarExpanded" />
        <IconSidebarExpand v-if="!sidebarExpanded" />
      </Button>

      <Logo class="logo" />
      <div class="page-header--mobile">
        <div class="page-header--mobile-left">
          <!-- TODO: Breadcrumbs -->
          <slot name="pageBreadcrumbs"></slot>
        </div>

        <div class="page-header--mobile-right">
          <Tooltip position="left">
            <template v-slot:text>
              <Button @click="launchTour" severity="secondary" :rounded="true">
                <template #iconLeft><IconHelp /></template>
              </Button>
            </template>
            <template v-slot:tooltip> Launch self-guided tour </template>
          </Tooltip>

          <IconNotifications />

          <IconSettings
            class="header-icon"
            @click="router.push({ name: 'settings' })"
            v-if="userStore.isStaff"
          />

          <UserMenu position="bottom">
            <template #button>
              <div class="user-avatar">
                {{ userStore.getInitials() }}
              </div>
            </template>
          </UserMenu>
        </div>
      </div>
    </div>

    <div class="mobile-header--links" :class="{ 'mobile-header--links-active': sidebarExpanded }">
      <ul class="sidebar--links">
        <!-- Overview -->
        <li
          v-if="!userStore.isOperationsUser"
          :class="{ 'sidebar-link--active': routeMenu === 'overview' }"
          data-step="0"
        >
          <RouterLink to="/">
            <IconOverview />
            <span class="sidebar-link-title">Overview</span>
          </RouterLink>
        </li>

        <!-- Scorecard -->
        <li
          v-if="!userStore.isOperationsUser && userStore.hasFeature('scorecard')"
          :class="{ 'sidebar-link--active': routeMenu === 'scorecard' }"
        >
          <RouterLink to="/scorecard">
            <IconScorecard />
            <span class="sidebar-link-title">Scorecard</span>
          </RouterLink>
        </li>

        <!-- Events -->
        <li
          v-if="!userStore.isOperationsUser"
          :class="{ 'sidebar-link--active': routeMenu === 'events' }"
          data-step="4"
        >
          <RouterLink v-if="userStore.hasScope('events.show')" to="/events">
            <IconCalendar />
            <span class="sidebar-link-title">Events</span>
          </RouterLink>
          <Tooltip v-else position="bottom">
            <template v-slot:text>
              <span class="sidebar-link--disabled">
                <IconCalendar />
                <span class="sidebar-link-title">Events</span>
              </span>
            </template>
            <template v-slot:tooltip> You do not have access to view this page. </template>
          </Tooltip>
        </li>

        <!-- Event Booking -->
        <FeatureFlag name="event_booking">
          <FeatureFlag name="client_profile">
            <li
              v-if="!userStore.isOperationsUser"
              class="with-submenu"
              :class="{ 'sidebar-link--active': (activeMenu || routeMenu) === 'booking' }"
              data-step="7"
            >
              <div @click="setMenu('booking')" class="sidebar-link--header">
                <IconBooking />
                <span class="sidebar-link-title">Booking</span>
                <IconCaretUp v-if="activeMenu === 'booking'" class="sidebar-header-collapse" />
              </div>

              <div class="submenu">
                <ul>
                  <li>
                    <RouterLink :to="`/booking/new`">
                      <span class="sidebar-link-title">New Quote</span>
                    </RouterLink>
                  </li>
                  <li>
                    <RouterLink to="/booking/drafts">
                      <span class="sidebar-link-title">Saved Quotes</span>
                    </RouterLink>
                  </li>
                  <li>
                    <RouterLink to="/booking/active">
                      <span class="sidebar-link-title">Active Bookings</span>
                    </RouterLink>
                  </li>
                </ul>
              </div>
            </li>
          </FeatureFlag>
        </FeatureFlag>

        <!-- Sensors -->
        <li
          v-if="!userStore.isOperationsUser"
          :class="{ 'sidebar-link--active': routeMenu === 'sensors' }"
          data-step="7"
        >
          <RouterLink v-if="userStore.hasScope('sensors.show')" to="/sensors">
            <IconSensors />
            <span class="sidebar-link-title">Sensors</span>
          </RouterLink>
          <Tooltip v-else position="bottom">
            <template v-slot:text>
              <span class="sidebar-link--disabled">
                <IconSensors />
                <span class="sidebar-link-title">Sensors</span>
              </span>
            </template>
            <template v-slot:tooltip> You do not have access to view this page. </template>
          </Tooltip>
        </li>

        <!-- Live Monitor -->
        <li :class="{ 'sidebar-link--active': routeMenu === 'live' }">
          <RouterLink to="/sensors/live">
            <IconLive />
            <span class="sidebar-link-title">Live Monitor</span>
          </RouterLink>
        </li>

        <!-- Users -->
        <li
          v-if="!userStore.isOperationsUser"
          :class="{ 'sidebar-link--active': routeMenu === 'users' }"
          data-step="6"
        >
          <RouterLink v-if="userStore.hasScope('users.show')" to="/users">
            <IconUsers />
            <span class="sidebar-link-title">Users</span>
          </RouterLink>
          <Tooltip v-else position="bottom">
            <template v-slot:text>
              <span class="sidebar-link--disabled">
                <IconUsers />
                <span class="sidebar-link-title">Users</span>
              </span>
            </template>
            <template v-slot:tooltip> You do not have access to view this page. </template>
          </Tooltip>
        </li>

        <!-- Accounts -->
        <li
          v-if="userStore.hasScope('accounts.show')"
          class="with-submenu"
          :class="{ 'sidebar-link--active': (activeMenu || routeMenu) === 'accounts' }"
        >
          <div @click="setMenu('accounts')" class="sidebar-link--header">
            <IconAccounts />
            <span class="sidebar-link-title">Accounts</span>
            <IconCaretUp v-if="activeMenu === 'accounts'" class="sidebar-header-collapse" />
          </div>

          <div class="submenu">
            <ul>
              <li>
                <RouterLink to="/accounts">
                  <IconEdit />
                  <span class="sidebar-link-title">Manage Accounts</span>
                </RouterLink>
              </li>
              <li>
                <RouterLink to="/roles">
                  <IconEdit />
                  <span class="sidebar-link-title">Manage Roles</span>
                </RouterLink>
              </li>
            </ul>
          </div>
        </li>

        <!-- Admin -->
        <FeatureFlag name="client_profile">
          <li
            v-if="userStore.isClientAdmin"
            class="with-submenu"
            :class="{ 'sidebar-link--active': (activeMenu || routeMenu) === 'admin' }"
          >
            <div @click="setMenu('admin')" class="sidebar-link--header">
              <IconAdmin />
              <span class="sidebar-link-title">Admin</span>
              <IconCaretUp v-if="activeMenu === 'admin'" class="sidebar-header-collapse" />
            </div>

            <div class="submenu">
              <ul>
                <li>
                  <RouterLink :to="`/admin/profile/${userStore.userData?.account_id}`">
                    <IconProfile />
                    <span class="sidebar-link-title">Profile</span>
                  </RouterLink>
                </li>
                <li>
                  <RouterLink to="/admin/documents">
                    <IconDocument />
                    <span class="sidebar-link-title">Documents</span>
                  </RouterLink>
                </li>
              </ul>
            </div>
          </li>
        </FeatureFlag>
      </ul>

      <ul class="sidebar--links sidebar--links-extra">
        <!-- FAQ -->
        <li :class="{ 'sidebar-link--active': routeMenu === 'faq' }" data-step="8">
          <RouterLink to="/faq">
            <IconFAQ />
            <span class="sidebar-link-title">FAQ</span>
          </RouterLink>
        </li>

        <!-- API -->
        <li :class="{ 'sidebar-link--active': routeMenu === 'api' }">
          <RouterLink to="/api">
            <IconAPI />
            <span class="sidebar-link-title">API</span>
          </RouterLink>
        </li>

        <!-- Contact -->
        <li :class="{ 'sidebar-link--active': routeMenu === 'contact' }">
          <RouterLink to="/contact">
            <IconContact />
            <span class="sidebar-link-title">Contact</span>
          </RouterLink>
        </li>
      </ul>

      <UserMenu position="top">
        <template #button>
          <div class="sidebar--user-info" data-step="9">
            <div class="sidebar--user-info--logo">
              <img v-if="getAccountLogo != ''" :src="getAccountLogo" />
            </div>
            <div class="sidebar--user-info--content">
              <div class="sidebar--user-info--name">
                {{ userStore.userData?.name }}
              </div>
              <div class="sidebar--user-info--role">
                {{ userStore.userData?.role_name }}
              </div>
            </div>
            <IconCaretDown />
          </div>
        </template>
      </UserMenu>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import { useUserStore } from '@/stores/user';

import Button from '@/components/global/Button.vue';
import FeatureFlag from '@/components/global/FeatureFlag.vue';
import IconBooking from '@/icons/sidebar/IconBooking.svg?component';
import UserMenu from '@/components/global/UserMenu.vue';
import Tooltip from '@/components/global/Tooltip.vue';
import Logo from '@/icons/Logo.svg?component';
import IconSidebarCollapse from '@/icons/sidebar/IconSidebarCollapse.svg?component';
import IconSidebarExpand from '@/icons/sidebar/IconSidebarExpand.svg?component';
import IconAccounts from '@/icons/sidebar/IconAccounts.svg?component';
import IconAdmin from '@/icons/sidebar/IconAdmin.svg?component';
import IconAPI from '@/icons/sidebar/IconAPI.svg?component';
import IconCalendar from '@/icons/sidebar/IconCalendar.svg?component';
import IconCaretUp from '@/icons/arrows/IconCaretUp.svg?component';
import IconCaretDown from '@/icons/arrows/IconCaretDown.svg?component';
import IconContact from '@/icons/sidebar/IconContact.svg?component';
import IconDocument from '@/icons/IconDocument.svg?component';
import IconEdit from '@/icons/IconEdit.svg?component';
import IconFAQ from '@/icons/sidebar/IconFAQ.svg?component';
import IconLive from '@/icons/sidebar/IconLive.svg?component';
import IconOverview from '@/icons/sidebar/IconOverview.svg?component';
import IconProfile from '@/icons/UserProfile.svg?component';
import IconScorecard from '@/icons/sidebar/IconScorecard.svg?component';
import IconSensors from '@/icons/sidebar/IconSensors.svg?component';
import IconUsers from '@/icons/sidebar/IconUsers.svg?component';
import IconHelp from '@/icons/header/IconHelp.svg?component';
import IconNotifications from '@/icons/header/IconNotifications.svg?component';
import IconSettings from '@/icons/header/IconSettings.svg?component';

const activeMenu = ref('');
const router = useRouter();
const route = useRoute();
const routeMenu = ref('');
const sidebarExpanded = ref(false);
const userStore = useUserStore();

const setMenu = (menu: string) => {
  if (activeMenu.value === menu) {
    activeMenu.value = '';
  } else {
    activeMenu.value = menu;
  }
};
if (route.path === '/') {
  routeMenu.value = userStore.hasFeature('scorecard') ? 'scorecard' : 'overview';
} else if (route.path === '/overview') {
  routeMenu.value = 'overview';
} else if (route.path.includes('scorecard')) {
  routeMenu.value = 'scorecard';
} else if (route.path.includes('purchasing')) {
  routeMenu.value = 'booking';
} else if (route.path.includes('events')) {
  routeMenu.value = 'events';
} else if (route.path.includes('live')) {
  routeMenu.value = 'live';
} else if (route.path.includes('sensors')) {
  routeMenu.value = 'sensors';
} else if (route.path.includes('users')) {
  routeMenu.value = 'users';
} else if (route.path.includes('admin')) {
  routeMenu.value = 'admin';
} else if (route.path.includes('accounts') || route.path.includes('roles')) {
  routeMenu.value = 'accounts';
} else if (route.path.includes('faq')) {
  routeMenu.value = 'faq';
} else if (route.path.includes('api')) {
  routeMenu.value = 'api';
} else if (route.path.includes('contact')) {
  routeMenu.value = 'contact';
}

const toggleSidebar = () => {
  sidebarExpanded.value = !sidebarExpanded.value;
};

const getAccountLogo = computed(() => {
  if (userStore.userData?.account_logo) {
    return `/media/${userStore.userData.account_logo}`;
  }
  return '';
});

const launchTour = () => {
  if (userStore.userData) {
    router.push({ name: 'overview', hash: '#tour' });
  }
};
</script>

<style lang="scss">
.mobile-header {
  position: sticky;
  top: 0;
  z-index: 100;

  .mobile-header--logo {
    @include mixins.flex-center($horiz: space-between);
    @include sizing.padding('lg');
    background: variables.$primitives-color-tertiary-600;
    color: variables.$primitives-color-background-100;
    height: 70px;
    position: sticky;
    top: 0;

    .logo {
      height: auto;
      margin-left: variables.$sizing-xl;
      width: 100px;
    }
  }

  .button.button--sidebar {
    @include mixins.flex-center($horiz: flex-end);
    @include sizing.padding('xxxs');
    background: variables.$primitives-color-background-200;
    color: variables.$primitives-color-tertiary-600;
    height: 30px;
    width: 30px;

    svg {
      height: variables.$sizing-sm;
      margin: 0;
      width: variables.$sizing-sm;
    }
  }

  .mobile-header--links {
    @include mixins.flex-center($horiz: flex-start);
    align-items: flex-start;
    background: variables.$primitives-color-tertiary-600;
    display: none;
    flex-direction: column;
    overflow: hidden;
    position: sticky;
    top: 70px;
    transition: width ease-in-out 200ms;
    white-space: nowrap;
    width: 0;
    will-change: width;
  }

  .mobile-header--links.mobile-header--links-active {
    display: flex;
    width: 100%;
  }

  .sidebar--links li a,
  .sidebar--links li span.sidebar-link--disabled,
  .sidebar--links li .sidebar-link--header {
    @include sizing.padding('xs', 'lg');
  }

  .sidebar--user-info {
    @include sizing.padding('xs', 'lg');
  }

  .sidebar--links .menu-container {
    width: 100%;

    .menu {
      @include sizing.margin(0, 'xs');
      min-width: calc(100% - 24px);
      width: calc(100% - 24px);
    }
  }
}

.page-header--mobile {
  display: none;
}

.mobile .page-header--mobile {
  @include mixins.flex-center($horiz: space-between);
  flex-basis: 100%;
  width: 100%;

  .page-header--mobile-right {
    @include mixins.flex-center($horiz: flex-end);
    color: variables.$primitives-color-tertiary-600;
    column-gap: variables.$sizing-xs;
  }

  .page-header--mobile-left {
    @include mixins.flex-center($horiz: flex-start);
    @include typography.font-body($weight: medium, $size: sm);
    color: variables.$primitives-color-tertiary-400;
    column-gap: variables.$sizing-xxxs;

    circle {
      display: none;
    }

    a {
      @include mixins.flex-center($horiz: flex-start);
      color: variables.$primitives-color-tertiary-400;
      margin-left: -4px;
    }

    span {
      color: variables.$primitives-color-primary-500;
    }
  }
}

.user-avatar {
  @include mixins.flex-center();
  background: variables.$primitives-color-secondary-500;
  border-radius: 50%;
  color: variables.$primitives-color-background-100;
  cursor: pointer;
  height: variables.$sizing-xl;
  width: variables.$sizing-xl;
}

.header-icon {
  cursor: pointer;
}
</style>
